<template>
  <b-modal
    v-model="showModal"
    :title="modalTitle"
    header-bg-variant="light"
    @ok.prevent="submitAdjustment"
    @hidden="resetForm"
    @cancel="cancelModal"
  >
    <b-form @submit.prevent="submitAdjustment">
      <b-form-group label="Điều chỉnh:">
        <b-form-radio-group
          v-model="form.adjustmentType"
          :options="[
            { text: 'Tăng điểm', value: 'add' },
            { text: 'Giảm điểm', value: 'subtract' },
          ]"
          stacked
        ></b-form-radio-group>
      </b-form-group>

      <b-form-group label="Số điểm:">
        <b-form-input
          v-model.number="form.points"
          type="number"
          min="1"
          :max="form.adjustmentType === 'subtract' ? currentPoints || 0 : null"
          required
          placeholder="Nhập số điểm"
        ></b-form-input>
        <small v-if="form.adjustmentType === 'subtract'" class="text-muted">
          Điểm hiện có: {{ currentPoints || 0 }}
        </small>
      </b-form-group>

      <b-form-group label="Lý do:">
        <b-form-input
          v-model="form.reason"
          required
          placeholder="Nhập lý do điều chỉnh"
        ></b-form-input>
      </b-form-group>

      <b-form-group label="Mô tả chi tiết:">
        <b-form-textarea
          v-model="form.description"
          rows="3"
          placeholder="Nhập mô tả chi tiết"
        ></b-form-textarea>
      </b-form-group>

      <b-form-group>
        <b-form-checkbox v-model="form.requiresApproval">
          Yêu cầu phê duyệt trước khi thực hiện
        </b-form-checkbox>
      </b-form-group>

      <div v-if="errorMessage" class="alert alert-danger">
        {{ errorMessage }}
      </div>
    </b-form>

    <template #modal-footer="{ cancel }">
      <b-button variant="secondary" @click="cancel()">
        Hủy
      </b-button>
      <b-button
        variant="info"
        @click="submitAdjustment"
        :disabled="!isFormValid || isSubmitting"
      >
        <b-spinner small v-if="isSubmitting" class="mr-1"></b-spinner>
        Điều chỉnh điểm
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { defineComponent, ref, computed, reactive, watch } from 'vue';
import ApiService from '@/core/services/api.service';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import emitter from '@/core/services/emitter';

export default defineComponent({
  name: 'PointAdjustmentModal',

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    customerId: {
      type: [String, Number],
      required: true,
    },
    currentPoints: {
      type: Number,
      default: 0,
    },
  },

  emits: ['update:show', 'adjustment-success'],

  setup(props, { emit }) {
    const showModal = computed({
      get: () => props.show,
      set: (value) => emit('update:show', value),
    });

    const modalTitle = ref('Điều chỉnh điểm');
    const isSubmitting = ref(false);
    const errorMessage = ref('');

    const form = reactive({
      adjustmentType: 'add',
      points: 0,
      reason: '',
      description: '',
      requiresApproval: false,
    });

    const isFormValid = computed(() => {
      if (!form.points || form.points <= 0) return false;
      if (!form.reason.trim()) return false;
      if (
        form.adjustmentType === 'subtract' &&
        form.points > props.currentPoints
      )
        return false;

      return true;
    });

    const resetForm = () => {
      form.adjustmentType = 'add';
      form.points = 0;
      form.reason = '';
      form.description = '';
      form.requiresApproval = false;
      errorMessage.value = '';
    };

    const cancelModal = () => {
      showModal.value = false;
      resetForm();
    };

    const submitAdjustment = async () => {
      if (!isFormValid.value) return;

      isSubmitting.value = true;
      errorMessage.value = '';

      try {
        const payload = {
          customerId: +props.customerId,
          points: form.adjustmentType === 'add' ? form.points : -form.points,
          reason: form.reason,
          description:
            form.description ||
            `${
              form.adjustmentType === 'add'
                ? 'Points added'
                : 'Points subtracted'
            }: ${form.reason}`,
          requiresApproval: form.requiresApproval,
        };

        const response = await ApiService.post(
          '/v1/customer-points/adjust',
          payload,
        );

        if (
          response.data &&
          (response.data.status === 200 || response.data.status === 1)
        ) {
          makeToastSuccess('Điều chỉnh điểm thành công');
          emit('adjustment-success');
          showModal.value = false;
          resetForm();
          emitter.emit('points-updated');
        } else {
          errorMessage.value =
            response.data?.message || 'Có lỗi xảy ra khi điều chỉnh điểm';
        }
      } catch (error) {
        console.error('Error adjusting points:', error);
        errorMessage.value =
          error.response?.data?.message || 'Không thể điều chỉnh điểm';
        makeToastFaile(errorMessage.value);
      } finally {
        isSubmitting.value = false;
      }
    };

    watch(
      () => props.currentPoints,
      (newPoints) => {
        if (form.adjustmentType === 'subtract' && form.points > newPoints) {
          form.points = newPoints;
        }
      },
    );

    return {
      showModal,
      modalTitle,
      form,
      isSubmitting,
      errorMessage,
      isFormValid,
      resetForm,
      cancelModal,
      submitAdjustment,
    };
  },
});
</script>
